import React, { useState, useEffect } from 'react';
import { X, Save, FileText, Upload, CheckCircle } from 'lucide-react';
import categories from '../../data/research_categories';

// ChipsInput component for multi-value fields
function ChipsInput({ values = [], onChange, placeholder }) {
  const [input, setInput] = useState('');

  // 确保 values 是数组
  const safeValues = Array.isArray(values) ? values : [];

  const handleKeyDown = (e) => {
    if ((e.key === 'Enter' || e.key === ',') && input.trim()) {
      e.preventDefault();
      if (!safeValues.includes(input.trim())) {
        onChange([...safeValues, input.trim()]);
      }
      setInput('');
    }
    if (e.key === 'Backspace' && !input && safeValues.length) {
      onChange(safeValues.slice(0, -1));
    }
  };
  return (
    <div className="flex flex-wrap items-center border rounded px-2 py-1 bg-white dark:bg-gray-700">
      {safeValues.map((val, idx) => (
        <span key={idx} className="bg-blue-100 text-blue-800 rounded px-2 py-1 mr-1 mb-1 flex items-center">
          {val}
          <button
            type="button"
            className="ml-1 text-blue-500 hover:text-blue-700"
            onClick={() => onChange(safeValues.filter((_, i) => i !== idx))}
          >
            ×
          </button>
        </span>
      ))}
      <input
        className="flex-1 outline-none bg-transparent py-1"
        value={input}
        onChange={e => setInput(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
      />
    </div>
  );
}

const EditSubmissionModal = ({ submission, isOpen, onClose, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    title: '',
    authorship_type: 'ai', // 添加缺失的字段
    authors: [], // 统一使用 authors 字段
    corresponding_author: '',
    category: [],
    abstract: '',
    keywords: [],
    license: 'CC-BY-4.0',
    is_public: 1, // 1 = public, 0 = private
  });const [newFile, setNewFile] = useState(null);
  const [validationError, setValidationError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  // Initialize form data when submission changes
  useEffect(() => {
    if (submission) {
      setFormData({
        title: submission.title || '',
        authorship_type: submission.authorship_type || 'ai',
        // 处理多种可能的作者字段名称
        authors: submission.authors || submission.agent_authors || submission.human_authors || [],
        corresponding_author: submission.corresponding_author || '',
        category: submission.category || [],
        abstract: submission.abstract || '',
        keywords: submission.keywords || [],
        license: submission.license || 'CC-BY-4.0',
        is_public: submission.is_public !== undefined ? submission.is_public : 1, // 保留原有的可见性设置
      });
    }
  }, [submission]);
  const handleCategoryChange = (index, value) => {
    const newCategory = [...formData.category];
    newCategory[index] = value;
    // 如果改变了上级分类，清空下级分类
    if (index === 0) {
      newCategory[1] = '';
      newCategory[2] = '';
    } else if (index === 1) {
      newCategory[2] = '';
    }
    setFormData({ ...formData, category: newCategory });
  };
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && (file.type === 'application/pdf' || file.name.endsWith('.tex'))) {
      setNewFile(file);
      // Note: File upload will be handled by the parent component during submission
    } else {
      alert('Please select a PDF or LaTeX (.tex) file.');
    }
  };const handleSubmit = async (e) => {
    e.preventDefault();
    
    // 验证必填字段
    const requiredFields = {
      'Title': formData.title,
      'Authors': formData.authors,
      'Corresponding Author': formData.corresponding_author,
      'Abstract': formData.abstract,
      'Keywords': formData.keywords,
      'License': formData.license,
    };

    const missingFields = Object.entries(requiredFields)
      .filter(([_, value]) => {
        if (Array.isArray(value)) {
          return value.length === 0;
        }
        return !value.trim();
      })
      .map(([key]) => key);

    if (missingFields.length > 0) {
      setValidationError(`Please fill out all required fields: ${missingFields.join(', ')}.`);
      return;
    }

    // 检查摘要字数
    const wordCount = Math.ceil(formData.abstract.length / 5);
    if (wordCount > 500) {
      setValidationError(`Abstract is too long. Maximum 500 words allowed (currently ${wordCount} words).`);
      return;
    }

    setValidationError('');
    setIsSubmitting(true);
      try {
      // 调用父组件的 onSubmit 函数并等待完成
      const result = await onSubmit(formData, newFile);
        // 检查是否成功
      if (result && result.success) {

        // 提交成功，显示成功状态
        setSubmissionSuccess(true);
        
        // 3秒后自动关闭弹窗
        setTimeout(() => {
          setSubmissionSuccess(false);
          setIsSubmitting(false);
          onClose();
        }, 3000);
      } else {
        throw new Error('Submission failed');
      }
      
    } catch (error) {
      console.error('Submission error:', error);
      setValidationError(error.message || 'Failed to submit. Please try again.');
      setIsSubmitting(false);
    }
  };if (!isOpen || !submission) return null;

  // 如果提交成功，显示成功弹窗
  if (submissionSuccess) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-8 text-center">
          <div className="mb-6">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Submission Updated Successfully!
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Your new version has been created and will be processed shortly.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const currentFileName = submission.s3_url ? submission.s3_url.split('/').pop() : 'No file';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Edit Submission
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              {submission.aixiv_id} • Version {submission.version} → New Version
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-8">
          {validationError && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md mb-6" role="alert">
              <strong className="font-bold">Error: </strong>
              <span className="block sm:inline">{validationError}</span>
            </div>
          )}
          
          <div className="space-y-6">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {submission.doc_type === 'paper' ? 'Paper' : 'Proposal'} Title *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                className="input-field"
                placeholder={submission.doc_type === 'paper' ? "Enter a descriptive title (max 220 characters)" : "Enter a descriptive proposal title (max 220 characters)"}
                maxLength={220}
              />
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {formData.title.length}/220 characters
              </div>
            </div>

            {/* Authorship Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Authorship Type *
              </label>
              <div className="flex items-center space-x-6">
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="authorship_type"
                    value="ai"
                    checked={formData.authorship_type === 'ai'}
                    onChange={(e) => setFormData({ ...formData, authorship_type: e.target.value })}
                    className="text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-gray-700 dark:text-gray-300">AI</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="authorship_type"
                    value="human"
                    checked={formData.authorship_type === 'human'}
                    onChange={(e) => setFormData({ ...formData, authorship_type: e.target.value })}
                    className="text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-gray-700 dark:text-gray-300">Human</span>
                </label>
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Select whether this submission was primarily generated by human researchers or by AI agents.
              </div>
            </div>

            {/* Authors */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {formData.authorship_type === 'human' ? 'Human Authors *' : 'AI Authors *'}
              </label>              <ChipsInput
                values={formData.authors}
                onChange={vals => setFormData({ ...formData, authors: vals })}
                placeholder={`Add ${formData.authorship_type === 'human' ? 'human' : 'AI'} author and press Enter`}
              />
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {formData.authorship_type === 'human'
                  ? 'Human authors are responsible for the research content and integrity.'
                  : 'AI or robotic authors must be explicitly identified as non-human contributors.'}
              </div>
            </div>


            {/* Corresponding Author */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Corresponding Author *
              </label>
              
              {/* 1. Replace <input> with the <ChipsInput> component */}
              <ChipsInput
                /* 2. Bind to the correct state. Convert the string to an array for the component to display */
                values={(formData.corresponding_author || '').split(',').filter(author => author.trim() !== '')}

                /* 3. CRITICAL: Convert the array from ChipsInput back into a single, comma-separated string before updating the state */
                onChange={vals => setFormData({ ...formData, corresponding_author: vals.join(', ') })}

                placeholder="Add author and press Enter"
              />
              {/* 4. Add the required instructional text below the component */}
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                The Corresponding Authors must be human and take responsibility for the Agent Authors.
              </div>
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Category *
              </label>
              <div className="grid grid-cols-3 gap-4">
                {/*  Level 1 */}
                <select
                  className="input-field"
                  value={formData.category[0] || ''}
                  onChange={e => {
                    const newCategory = [e.target.value, '', ''];
                    setFormData({ ...formData, category: newCategory });
                  }}
                >
                  <option value="">Select main category</option>
                  {Object.keys(categories).map(main => (
                    <option key={main} value={main}>{main}</option>
                  ))}
                </select>

                {/*  Level 2 */}
                <select
                  className="input-field"
                  value={formData.category[1] || ''}
                  onChange={e => {
                    const newCategory = [formData.category[0], e.target.value, ''];
                    setFormData({ ...formData, category: newCategory });
                  }}
                  disabled={!formData.category[0]}
                >
                  <option value="">Select subcategory</option>
                  {formData.category[0] &&
                    Object.keys(categories[formData.category[0]]).map(sub => (
                      <option key={sub} value={sub}>{sub}</option>
                    ))}
                </select>

                {/*  Level 3 */}
                <select
                  className="input-field"
                  value={formData.category[2] || ''}
                  onChange={e => {
                    const newCategory = [formData.category[0], formData.category[1], e.target.value];
                    setFormData({ ...formData, category: newCategory });
                  }}
                  disabled={!formData.category[1]}
                >
                  <option value="">Select specialization</option>
                  {formData.category[0] &&
                    formData.category[1] &&
                    categories[formData.category[0]][formData.category[1]].map(spec => (
                      <option key={spec} value={spec}>{spec}</option>
                    ))}
                </select>
              </div>
            </div>

            {/* Abstract */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Abstract *
              </label>
              <textarea
                value={formData.abstract}
                onChange={(e) => setFormData({...formData, abstract: e.target.value})}
                className="input-field"
                placeholder="Enter a comprehensive abstract (max 500 words)"
                rows="6"
                maxLength={2500}
              />
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {formData.abstract.length} characters • {Math.ceil(formData.abstract.length / 5)} words (max 500 words)
              </div>
            </div>

            {/* Keywords */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Keywords * (3-6 recommended)
              </label>
              <ChipsInput
                values={formData.keywords}
                onChange={vals => setFormData({ ...formData, keywords: vals })}
                placeholder="Add keyword and press Enter"
              />
              <div className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                Suggested: machine learning, neural networks, transformers
              </div>
            </div>            {/* File Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Submission File
              </label>              <div className="flex items-center space-x-4">
                <FileText className="h-5 w-5 text-gray-400" />
                <span className="text-sm text-gray-600 dark:text-gray-300 flex-1 truncate">
                  Current: {currentFileName}
                </span>
                <input
                  type="file"
                  id="file-upload"
                  className="hidden"
                  onChange={handleFileChange}
                  accept=".pdf,.tex"
                />
                <label
                  htmlFor="file-upload"
                  className="btn-secondary cursor-pointer"
                >
                  Replace File
                </label>
              </div>

              {/* Show selected new file */}
              {newFile && (
                <div className="mt-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <Upload className="h-4 w-4 text-blue-600" />
                    <span className="text-sm text-blue-800 dark:text-blue-200">
                      New file selected: {newFile.name}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* License */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                License
              </label>
              <select 
                value={formData.license}
                onChange={(e) => setFormData({...formData, license: e.target.value})}
                className="input-field"
              >
                <option value="CC-BY-4.0">CC BY 4.0 (Recommended)</option>
                <option value="CC-BY-SA-4.0">CC BY-SA 4.0</option>
                <option value="CC-BY-NC-4.0">CC BY-NC 4.0</option>
                <option value="MIT">MIT</option>
                <option value="Apache-2.0">Apache 2.0</option>
              </select>
            </div>

            {/* Visibility */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Visibility *
              </label>
              <div className="space-y-3">
                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    name="is_public"
                    value="1"
                    checked={formData.is_public === 1}
                    onChange={() => setFormData({ ...formData, is_public: 1 })}
                    className="mt-1 text-primary-600 focus:ring-primary-500"
                  />
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <span className="font-medium text-gray-700 dark:text-gray-300">Public</span>
                      <span className="px-2 py-0.5 bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded text-xs font-medium">
                        Recommended
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      Your research will be publicly visible and accessible to everyone
                    </p>
                  </div>
                </label>
                
                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    name="is_public"
                    value="0"
                    checked={formData.is_public === 0}
                    onChange={() => setFormData({ ...formData, is_public: 0 })}
                    className="mt-1 text-primary-600 focus:ring-primary-500"
                  />
                  <div className="flex-1">
                    <span className="font-medium text-gray-700 dark:text-gray-300">Private</span>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      Your research will be visible only to you until you make it public
                    </p>
                  </div>
                </label>
              </div>
              
              <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  💡 <strong>Tip:</strong> We recommend making your research public to maximize visibility and impact. You can keep it private during initial development and make your best version public when ready.
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end space-x-3 mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onCancel}
              className="btn-secondary"
            >
              Cancel
            </button>            <button
              type="submit"
              disabled={isSubmitting}
              className={`btn-primary flex items-center space-x-2 ${
                isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <Save className="h-4 w-4" />
              <span>{isSubmitting ? 'Creating...' : 'Create New Version'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditSubmissionModal;